import moment from 'moment';

export const RANGE_PRESETS = ['Current week', 'Current month', 'Last week', 'Last month', 'Last 3 months'] as const;
export type RangePreset = typeof RANGE_PRESETS[number];
export type ChartRange = {startDate: string, endDate: string, preset?: RangePreset};
export type HistoryGrouping = 'DAY' | 'WEEK' | 'MONTH';
export type ChartLabelOptions = {groupBy: HistoryGrouping, includeYear: boolean};
const ISO_DATE = 'YYYY-MM-DD';
const PRESET_GROUPINGS: Record<RangePreset, HistoryGrouping> = {
    'Current week': 'DAY',
    'Last week': 'DAY',
    'Current month': 'WEEK',
    'Last month': 'WEEK',
    'Last 3 months': 'MONTH',
};

export const todayUtc = () => moment.utc().format(ISO_DATE);
export const minimumDate = (today: string) => moment.utc(today).subtract(3, 'years').format(ISO_DATE);
export const toCalendarDate = (value: string) => value ? new Date(`${value}T00:00:00`) : undefined;
export const toDateFilter = (value?: Date) => value
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';

export const getPresetRange = (preset: RangePreset, today: string): ChartRange => {
    const start = moment.utc(today);
    const end = start.clone();
    switch (preset) {
        case 'Current week': start.startOf('isoWeek'); break;
        case 'Current month': start.startOf('month'); break;
        case 'Last week':
            start.subtract(1, 'week').startOf('isoWeek');
            end.startOf('isoWeek').subtract(1, 'day');
            break;
        case 'Last month':
            start.subtract(1, 'month').startOf('month');
            end.startOf('month').subtract(1, 'day');
            break;
        case 'Last 3 months': start.subtract(3, 'months'); break;
    }
    return {startDate: start.format(ISO_DATE), endDate: end.format(ISO_DATE), preset};
};

export const getDefaultGrouping = ({preset, startDate, endDate}: ChartRange): HistoryGrouping => {
    if (preset) {
        return PRESET_GROUPINGS[preset];
    }
    const days = moment.utc(endDate).diff(moment.utc(startDate), 'days') + 1;
    if (days <= 7) {
        return 'DAY';
    }
    return days <= 31 ? 'WEEK' : 'MONTH';
};

export const getRangeError = ({startDate, endDate}: ChartRange, today: string): string | null => {
    if (!moment.utc(startDate, ISO_DATE, true).isValid() || !moment.utc(endDate, ISO_DATE, true).isValid()) {
        return 'Enter a valid From and To date.';
    }
    if (startDate < minimumDate(today) || endDate < minimumDate(today)) {
        return 'Dates cannot be more than 3 years before today.';
    }
    if (startDate > today || endDate > today) {
        return 'Dates cannot be in the future.';
    }
    return startDate > endDate ? 'From must be on or before To.' : null;
};

export const formatPeriod = (start: string, end: string, includeYear = false) => {
    const format = includeYear || start.slice(0, 4) !== end.slice(0, 4) ? 'MMM D, YYYY' : 'MMM D';
    const first = moment.utc(start).format(format);
    return start === end ? first : `${first} – ${moment.utc(end).format(format)}`;
};

export const formatChartPeriod = (start: string, end: string, {groupBy, includeYear}: ChartLabelOptions) =>
    groupBy === 'MONTH' ? moment.utc(start).format('MMM YYYY') : formatPeriod(start, end, includeYear);
